import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, SealCheck, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { miscApi } from "@/api";

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-serif text-2xl mt-6 mb-2 text-primary">{line.slice(3)}</h2>;
    if (line.startsWith("# ")) return <h1 key={i} className="font-serif text-3xl mt-6 mb-3">{line.slice(2)}</h1>;
    if (line.startsWith("- ")) return <li key={i} className="ml-5 list-disc text-sm mb-1">{line.slice(2)}</li>;
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="text-sm leading-relaxed mb-2">{line}</p>;
  });
}

export default function Report({ finalCheck }) {
  const { pid } = useParams();
  const [report, setReport] = useState(null);
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!finalCheck) miscApi.report(pid).then((r) => setReport(r.content ? r : null)).catch(() => {});
  }, [pid, finalCheck]);

  const build = async () => {
    setLoading(true);
    try {
      const r = await miscApi.buildReport(pid);
      setReport(r);
      toast.success("Rapport généré");
    } catch (e) { toast.error("Échec de la génération"); }
    finally { setLoading(false); }
  };

  const runCheck = async () => {
    setLoading(true);
    try {
      const r = await miscApi.finalCheck(pid);
      setCheck(r);
    } finally { setLoading(false); }
  };

  if (finalCheck) {
    const colors = { green: "#10B981", yellow: "#D97706", red: "#DC2626" };
    const labels = { green: "🟢", yellow: "🟡", red: "🔴" };
    return (
      <div className="p-8 max-w-3xl fade-up">
        <p className="overline text-primary mb-1">Contrôle final</p>
        <h1 className="font-serif text-4xl font-light tracking-tight mb-6">Contrôle final du manuscrit</h1>
        <p className="text-muted-foreground mb-6">EDITORIX effectue une nouvelle analyse globale : fautes restantes, incohérences, répétitions, chronologie, personnages et structure.</p>
        <Button onClick={runCheck} disabled={loading} className="rounded-sm gap-2 mb-8" data-testid="run-final-check">
          <SealCheck size={18} weight="duotone" /> {loading ? "Analyse…" : "Lancer le contrôle final"}
        </Button>

        {check && (
          <div className="border-2 rounded-lg p-8 text-center fade-up" style={{ borderColor: colors[check.status] }} data-testid="final-check-result">
            <div className="text-5xl mb-3">{labels[check.status]}</div>
            <h2 className="font-serif text-3xl mb-2" style={{ color: colors[check.status] }}>{check.label}</h2>
            <p className="text-muted-foreground mb-4">{check.message}</p>
            <div className="flex justify-center gap-6 text-sm font-mono border-t border-border pt-4 mt-4">
              <span>Analysés : {check.analyzed}/{check.total}</span>
              <span style={{ color: "#DC2626" }}>Erreurs : {check.errors}</span>
              <span style={{ color: "#4F46E5" }}>Incohérences : {check.inconsistencies}</span>
              <span style={{ color: "#EAB308" }}>Répétitions : {check.repetitions}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-6 italic">{check.note}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="overline text-primary mb-1">Rapport éditorial EDITORIX</p>
          <h1 className="font-serif text-4xl font-light tracking-tight">Rapport</h1>
        </div>
        <Button onClick={build} disabled={loading} className="rounded-sm gap-2" data-testid="build-report">
          <Sparkle size={16} weight="duotone" /> {loading ? "Génération…" : report ? "Régénérer" : "Générer le rapport"}
        </Button>
      </div>

      {!report ? (
        <div className="border border-dashed border-border rounded-lg py-16 text-center text-muted-foreground">
          <FileText size={40} weight="duotone" className="mx-auto mb-3 opacity-50" />
          Aucun rapport encore. Générez un rapport éditorial complet basé sur l'analyse du manuscrit.
        </div>
      ) : (
        <div className="border border-border bg-surface rounded-lg p-8" data-testid="report-content">
          {report.stats && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6 pb-6 border-b border-border">
              {[["Chapitres", report.stats.total], ["Analysés", report.stats.analyzed], ["Erreurs", report.stats.errors], ["Suggestions", report.stats.suggestions], ["Répétitions", report.stats.repetitions], ["Incohérences", report.stats.inconsistencies]].map(([l, v]) => (
                <div key={l}><p className="overline text-muted-foreground">{l}</p><p className="font-serif text-2xl">{v}</p></div>
              ))}
            </div>
          )}
          <div className="prose-editorix">{renderMarkdown(report.content)}</div>
          <p className="text-xs text-muted-foreground mt-6">Généré le {new Date(report.generated_at).toLocaleString("fr")}</p>
        </div>
      )}
    </div>
  );
}
